import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons/faCalendarCheck";
import { faCalendarClock } from "@fortawesome/pro-regular-svg-icons/faCalendarClock";
import { faCalendarXmark } from "@fortawesome/pro-regular-svg-icons/faCalendarXmark";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons/faCircleExclamation";
import { faDesktop } from "@fortawesome/pro-regular-svg-icons/faDesktop";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons/faLocationDot";
import { faUserCheck } from "@fortawesome/pro-regular-svg-icons/faUserCheck";
import { Grid, Stack, Typography } from "@mui/material";
import { MprcLink } from "components";
import { AddressModel, EnrollmentViewDto, EnrollStatus, MeetingAttendanceRegisterMode, MeetingType, TrainingContentType, TrainingMeetingItemDto } from "generated/CoreApi_v1";
import { TFunction } from "i18next";
import { Formaters } from "lib/Formaters";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import TzUtils from "lib/TzUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MprcCardCourseIcon } from "views/lms/student/EnrollmentView/Course/ContentComponents/common/CourseViewComponents";
import { EnrollmentStatus } from "views/lms/student/EnrollmentView/Shared/EnrollmentStatus";
import { TrainingDueDate } from "views/lms/student/EnrollmentView/Shared/TrainingDueDate";

/////////////////////////////////////////////////////////////////////////////////////

export function meetingLocationName(meetingType: MeetingType, locationName: string | null, t: TFunction) {
    return meetingType == MeetingType.Teams
        ? t("Student.EnrollmentView.Content.Meeting.TeamsText")
        : meetingType == MeetingType.Zoom
            ? t("Student.EnrollmentView.Content.Meeting.ZoomText")
            : meetingType == MeetingType.Other
                ? t("Student.EnrollmentView.Content.Meeting.OnlineText")
                : locationName && locationName.length > 0
                    ? locationName
                    : t("Training.Edit.StepCourseContent.MeetingNoLocationText");
}

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    meeting: TrainingMeetingItemDto;
    classroomId?: string | null;
    enrollment?: EnrollmentViewDto;
}

/////////////////////////////////////////////////////////////////////////////////////

export const TrainingMeetingDataBox = (props: IProps) => {

    const { meeting, enrollment, classroomId } = props;
    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();

    var timezoneOffsetName = useMemo(() => {
        return meeting.meetingType == MeetingType.Presential && TzUtils.browserTimezone() != meeting.timezoneId
            ? meeting.startDate?.getTimezoneOffsetName(meeting.timezoneId)
            : undefined;
    }, [meeting]);

    ///
    const handleShowMap = (address: AddressModel) => {
        HtmlUtils.openGoogleMaps(address.address1, address.city, address.state);
    };

    ///
    const copyLink = async () => {
        var url = meeting.meetingOnlineLink;

        if (meeting.isInstructor && meeting.meetingOnlineLinkExpired && classroomId) {
            try {
                const response = await api.v1.trainingManagement.updateZoomInstructorLinkCommand({ classroomId, componentId: meeting.componentId });
                if (response.data != null) url = response.data;
            }
            catch {
                enqueueSnackbar(t("Student.EnrollmentView.Content.Meeting.CannotUpdateInstructorLink"), { variant: "error" });
                return;
            }
        }

        if (url != null) {
            HtmlUtils.copyToClipboard(url);
            enqueueSnackbar(t("Student.EnrollmentView.Content.Meeting.CopyLinkSuccessMessage"), { variant: "success" });
        }
    };

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderCancelledBox = () => {
        return (
            <Grid container spacing={1}>
                <Grid>
                    <MprcCardCourseIcon icon={faCalendarXmark} />
                </Grid>
                <Grid size="grow">
                    <Typography variant="h6" color="error.main" textTransform="uppercase" fontWeight="bold">
                        {t("Student.EnrollmentView.Content.Meeting.CancelledText")}
                    </Typography>
                    {meeting.enrollmentData?.cancelReason &&
                        <Typography variant="caption" component="p">
                            {meeting.enrollmentData?.cancelReason}
                        </Typography>
                    }
                </Grid>
            </Grid>
        );
    };

    const RenderPresence = (
        <Grid container spacing={1}>
            <Grid>
                <MprcCardCourseIcon icon={faUserCheck} />
            </Grid>
            <Grid>
                {meeting.enrollmentData?.attended == null
                    ?
                    <>
                        <Typography variant="h6" color="muted.main" textTransform="uppercase">
                            {t("Student.EnrollmentView.Content.Meeting.PresenceText")}
                        </Typography>
                        <Typography variant="caption">
                            {t("Student.EnrollmentView.Content.Meeting.PresenceNotDefinedText")}
                        </Typography>
                    </>
                    : <>
                        <Typography variant="overline" component="p" color={meeting.enrollmentData?.attended ? "success.main" : "error.main"}>
                            {t(meeting.enrollmentData?.attended ? "Student.EnrollmentView.Content.Meeting.PresentText" : "Student.EnrollmentView.Content.Meeting.AbsentText")}
                        </Typography>
                        {meeting.enrollmentData?.registerMode != null &&
                            <Typography variant="caption">
                                {
                                    meeting.enrollmentData!.registerMode == MeetingAttendanceRegisterMode.ByInstructor
                                        ? t("Student.EnrollmentView.Content.Meeting.RegisterByInstructorText")
                                        : meeting.enrollmentData!.registerMode == MeetingAttendanceRegisterMode.ByQrCode
                                            ? t("Student.EnrollmentView.Content.Meeting.RegisterByQRCodeText")
                                            : meeting.enrollmentData!.registerMode == MeetingAttendanceRegisterMode.ByRecordingView
                                                ? t("Student.EnrollmentView.Content.Meeting.RegisterRecordingText")
                                                : meeting.enrollmentData!.registerMode == MeetingAttendanceRegisterMode.ByIntegration
                                                    ? t("Student.EnrollmentView.Content.Meeting.RegisterByIntegrationText", { name: meeting.meetingType == MeetingType.Teams ? "Teams" : "Zoom" })
                                                    : meeting.enrollmentData!.registerMode == MeetingAttendanceRegisterMode.BySystem
                                                        ? t("Student.EnrollmentView.Content.Meeting.RegisterBySystem")
                                                        : null
                                }
                            </Typography>
                        }
                    </>
                }
            </Grid>
        </Grid>
    );

    const RenderMeetinScore = (
        <Grid container spacing={1}>
            {meeting.enrollmentData?.scorePending == true &&
                <>
                    <Grid>
                        <MprcCardCourseIcon icon={faCircleExclamation} />
                    </Grid>
                    <Grid>
                        <Typography variant="h6" color="muted.main" textTransform="uppercase">
                            {t("Student.EnrollmentView.Content.Meeting.ScoreText")}
                        </Typography>
                        <Typography variant="caption" component="p">
                            {t("Student.EnrollmentView.Content.Meeting.ScorePendingText")}
                        </Typography>
                    </Grid>
                </>
            }
            {meeting.enrollmentData?.scorePending == false && meeting.enrollmentData?.scorePercent != null &&
                <>
                    <Grid>
                        <MprcCardCourseIcon icon={faCircleExclamation} />
                    </Grid>
                    <Grid>
                        <Typography
                            variant="h6"
                            textTransform="uppercase"
                            fontWeight="bold"
                            color={meeting.enrollmentData?.scorePercent >= meeting.enrollmentData?.minFinalScore ? "success.main" : "error.main"}
                        >
                            {Formaters.percentString(meeting.enrollmentData?.scorePercent ?? 0, 1)}
                        </Typography>
                        <Typography variant="caption" component="p">
                            {t("Student.EnrollmentView.Content.Meeting.ScoreParticipationText")}
                        </Typography>
                        {
                            (
                                enrollment?.trainingContentType == TrainingContentType.LiveOnline
                                ||
                                enrollment?.trainingContentType == TrainingContentType.LivePresential
                            ) &&
                            meeting.enrollmentData?.scorePercent < meeting.enrollmentData?.minFinalScore &&
                            <Typography variant="caption" component="p">
                                {t("Student.EnrollmentView.Content.Meeting.MinFinalScoreText", { count: meeting.enrollmentData?.minFinalScore ?? 0 })}
                            </Typography>
                        }
                    </Grid>
                </>
            }
        </Grid>
    );

    ///
    const RenderCompleted = () => {
        return (
            <Grid container spacing={1}>
                <Grid>
                    <MprcCardCourseIcon icon={faCalendarCheck} />
                </Grid>
                <Grid size="grow">
                    <Typography variant="h6" color="success.main" textTransform="uppercase" fontWeight="bold">
                        {t("Student.EnrollmentView.Content.Meeting.FinishedText")}
                    </Typography>
                    {
                        meeting.startDate && meeting.endDate &&
                        <>
                            {
                                LibUtils.isSameDay(meeting.startDate, meeting.endDate)
                                    ? (
                                        <Typography variant="caption" component="p">
                                            {
                                                meeting.meetingType == MeetingType.Presential
                                                    ? (
                                                        <>
                                                            {Formaters.performaFullDateRange(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, { longNames: true, dayOfWeek: true })}
                                                            &nbsp;
                                                            {timezoneOffsetName}
                                                        </>
                                                    )
                                                    : Formaters.performaFullDateRange(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, { replaceClosestDays: false })
                                            }
                                        </Typography>
                                    )
                                    : (
                                        <>
                                            <Typography variant="caption" component="p">
                                                {
                                                    meeting.meetingType == MeetingType.Presential
                                                        ? (
                                                            <>
                                                                {Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))}
                                                                &nbsp;
                                                                {timezoneOffsetName}
                                                            </>
                                                        )
                                                        : <span>{Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))}</span>
                                                }
                                                <span>&nbsp;{t("Literal.Until", { ns: "shared" })}&nbsp;</span>
                                            </Typography>
                                            <Typography variant="caption" component="p">
                                                {
                                                    meeting.meetingType == MeetingType.Presential
                                                        ? (
                                                            <>
                                                                {Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))}
                                                                &nbsp;
                                                                {timezoneOffsetName}
                                                            </>
                                                        )
                                                        : Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))
                                                }
                                            </Typography>
                                        </>
                                    )
                            }
                        </>
                    }
                    <Typography variant="caption" component="p">
                        {meetingLocationName(meeting.meetingType, meeting.locationName, t)}
                    </Typography>
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderFinishedBox = () => {
        return (
            <Grid container direction="column" spacing={2} width="100%">
                <Grid>
                    {(enrollment?.status == EnrollStatus.Approved || enrollment?.status == EnrollStatus.Reproved)
                        ? <EnrollmentStatus variant="block" data={enrollment!} />
                        : enrollment?.totalComponents === 1
                            ? <TrainingDueDate enrollment={enrollment} />
                            : RenderCompleted()
                    }
                </Grid>
                <Grid size="grow">
                    <Stack id="presenceMeetingStack" direction="row" gap={2} alignItems="start" justifyContent="space-between" width="100%">
                        {RenderPresence}
                        {RenderMeetinScore}
                    </Stack>
                </Grid>
            </Grid >
        );
    };

    ///
    const RenderPendingBox = () => {
        return (
            <Grid container direction="column" spacing={2} wrap="nowrap">
                {meeting.startDate && meeting.endDate &&
                    <Grid>
                        <Grid container spacing={1}>
                            <Grid>
                                <MprcCardCourseIcon icon={faCalendarClock} />
                            </Grid>
                            <Grid>
                                <Grid container direction="column" spacing={0}>
                                    {LibUtils.isSameDay(meeting.startDate, meeting.endDate)
                                        ? (
                                            <Grid>
                                                <Typography variant="subtitle1" component="span" color="warning.main" fontWeight="bold">
                                                    {meeting.meetingType == MeetingType.Presential
                                                        ? <>
                                                            {Formaters.performaFullDateRange(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, { longNames: true, dayOfWeek: true, replaceClosestDays: false })}
                                                            &nbsp;
                                                            {timezoneOffsetName}
                                                        </>
                                                        : Formaters.performaFullDateRange(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, { longNames: true, dayOfWeek: true, replaceClosestDays: false })
                                                    }
                                                </Typography>
                                            </Grid>
                                        )
                                        : (
                                            <>
                                                <Grid>
                                                    <Typography variant="subtitle1" component="span" color="warning.main" fontWeight="bold">
                                                        {meeting.meetingType == MeetingType.Presential
                                                            ? <>
                                                                {Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))}
                                                                &nbsp;
                                                                {timezoneOffsetName}
                                                            </>
                                                            : <span>{Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.startDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))}</span>
                                                        }
                                                        <span>&nbsp;{t("Literal.Until", { ns: "shared" })}&nbsp;</span>
                                                    </Typography>
                                                </Grid>
                                                <Grid>
                                                    <Typography variant="subtitle1" component="p" color="warning.main" fontWeight="bold">
                                                        {meeting.meetingType == MeetingType.Presential
                                                            ? <>
                                                                {Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))}
                                                                &nbsp;
                                                                {timezoneOffsetName}
                                                            </>
                                                            : Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(meeting.endDate, meeting.timezoneId)!, true, true, t("shared:Literal.AtTime"))
                                                        }
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )
                                    }
                                    {meeting.mandatoryAttendance &&
                                        <Grid>
                                            <Typography variant="caption" component="p" textTransform="uppercase" color="warning.main">
                                                {t("Student.EnrollmentView.Content.Meeting.MandatoryText")}
                                            </Typography>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                <Grid>
                    {meeting.meetingType == MeetingType.Presential
                        ?
                        <Grid container spacing={1}>
                            <Grid>
                                <MprcCardCourseIcon icon={faLocationDot} />
                            </Grid>
                            <Grid>
                                <Typography variant="body2" fontWeight="bold" color="black">
                                    {meeting.locationName
                                        ? <span>{meeting.locationName}</span>
                                        : <span>{t("Training.Edit.StepCourseContent.MeetingNoLocationText")}</span>
                                    }
                                </Typography>
                                {meeting.address && meeting.address != null &&
                                    <MprcLink onClick={() => handleShowMap(meeting.address!)}>
                                        <Typography variant="caption" component="p" color="highlight.main">
                                            {t("Student.EnrollmentView.Content.Meeting.ViewMapText")}
                                        </Typography>
                                    </MprcLink>
                                }
                            </Grid>
                        </Grid>
                        :
                        <Grid container spacing={1}>
                            <Grid>
                                <MprcCardCourseIcon icon={faDesktop} />
                            </Grid>
                            <Grid>
                                <Typography variant="h6" color="muted.main" textTransform="uppercase">
                                    {t(meeting.meetingType == MeetingType.Teams
                                        ? "Student.EnrollmentView.Content.Meeting.TeamsText"
                                        : meeting.meetingType == MeetingType.Zoom
                                            ? "Student.EnrollmentView.Content.Meeting.ZoomText"
                                            : "Student.EnrollmentView.Content.Meeting.OnlineText"
                                    )}
                                </Typography>
                                {meeting.meetingOnlineLink && meeting.meetingOnlineLink != null &&
                                    <MprcLink onClick={() => copyLink()} disabled={enrollment?.readOnly === true}>
                                        <Typography variant="caption" component="p" color="highlight.main">
                                            {t("Student.EnrollmentView.Content.Meeting.CopyLinkMeetingText")}
                                        </Typography>
                                    </MprcLink>
                                }
                            </Grid>
                        </Grid>
                    }
                </Grid>
                <Grid>
                    {meeting.enrollmentData?.attended == true && RenderPresence}
                </Grid>
            </Grid >
        );
    };

    /////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    /////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {meeting.enrollmentData?.cancelledOn
                ? RenderCancelledBox()
                : meeting.finished
                    ? RenderFinishedBox()
                    : RenderPendingBox()
            }
        </>
    );
};
